// v222 / v223 — Build-Info detail page (item 33 of
// docs/PRODUCT-ROADMAP.md).
//
// Phase A renders one build's metadata + denormalised artifact list
// + properties + the original JFrog-format JSON body the CI
// submitted. Phase B (v223) folds in a "Promote build" section at
// the top so an operator can copy every artifact this build
// produced into a downstream repo (e.g. staging → prod) without
// having to hand-pick artifact UUIDs from the existing /promotions
// flow.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  ExternalLink,
  GitBranch,
  Box,
  Tag,
  ChevronDown,
  ChevronRight,
  Rocket,
  Download,
  Layers,
  Search,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
  buildSBOMUrl,
  promoteBuild,
  type PromoteResponse,
  useBuildDetail,
} from '../../api/buildInfo';
import { useRepos, type Repo } from '../../api/client';

function shortRev(rev?: string): string {
  if (!rev) return '';
  return rev.length > 12 ? rev.slice(0, 12) : rev;
}

export function BuildDetail() {
  const { id, name, number } = useParams<{
    id: string;
    name: string;
    number: string;
  }>();
  const { data, error, isLoading } = useBuildDetail(id, name, number);
  const [showBody, setShowBody] = useState(false);

  // Promote-state. Lives at the page level so the form stays
  // mounted even after a successful promote (operator can run a
  // second promote into a different repo without losing the
  // dropdown selection).
  const { data: allRepos } = useRepos();
  const projectRepos = useMemo<Repo[]>(
    () => (allRepos ?? []).filter((r) => r.projectId === id),
    [allRepos, id],
  );
  const [targetRepoID, setTargetRepoID] = useState('');
  const [requireApproval, setRequireApproval] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PromoteResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function onSubmitPromote(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !name || !number || !targetRepoID) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    try {
      const r = await promoteBuild(id, name, number, {
        target_repo_id: targetRepoID,
        require_approval: requireApproval,
        note: note.trim() || undefined,
      });
      setResult(r);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to={`/projects/${id}/builds`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to builds
        </Link>
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <p className="text-fg-muted text-sm">Loading…</p>
      </div>
    );
  }

  const propEntries = Object.entries(data.properties).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const sbomHref = id && name && number ? buildSBOMUrl(id, name, number) : '';
  const sbomFilename = `${(name ?? 'build').replace(/[\\/:"]/g, '_')}-${(
    number ?? 'n'
  ).replace(/[\\/:"]/g, '_')}.cdx.json`;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/projects/${id}/builds`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to builds
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="orb-eyebrow text-accent/80 mb-1">Build · #{data.number}</p>
            <h1 className="text-2xl font-display font-semibold text-fg leading-tight inline-flex items-center gap-2">
              <Activity className="h-5 w-5 text-accent" />
              {data.name}
            </h1>
            <p className="text-fg-muted text-sm mt-2">
              Started {new Date(data.started).toLocaleString()} · submitted{' '}
              {new Date(data.created_at).toLocaleString()}
            </p>
          </div>
          {sbomHref && (
            <a
              href={sbomHref}
              download={sbomFilename}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
              title="CycloneDX 1.5 SBOM aggregating every artifact + dependency this build touched"
            >
              <Download className="h-4 w-4" />
              Export SBOM (CycloneDX)
            </a>
          )}
        </div>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Rocket className="h-4 w-4 text-accent" />
              Promote build
            </span>
          }
          description="Copy every artifact this build produced into a downstream repo. The byte payloads are content-addressable so the underlying S3 objects are shared — only metadata rows are written."
        />
        <CardBody>
          <form onSubmit={onSubmitPromote} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-fg-muted text-xs mb-1">
                  Target repo
                </span>
                <select
                  className="w-full h-10 px-3 rounded-md bg-bg-elevated border border-border text-fg focus:outline-none focus:border-accent"
                  value={targetRepoID}
                  onChange={(e) => setTargetRepoID(e.target.value)}
                  required
                >
                  <option value="">— pick a repo —</option>
                  {projectRepos.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.slug} ({r.format} · {r.kind})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-fg-muted text-xs mb-1">
                  Note (optional)
                </span>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. release-v1.4 cut"
                />
              </label>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-fg-muted cursor-pointer">
              <input
                type="checkbox"
                checked={requireApproval}
                onChange={(e) => setRequireApproval(e.target.checked)}
              />
              {' '}
              Require approval (lands as <code className="font-mono">pending</code>;
              an operator must approve via /promotions before bytes copy)
            </label>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="primary"
                disabled={!targetRepoID || submitting}
              >
                {submitting ? 'Promoting…' : 'Promote build'}
              </Button>
              {projectRepos.length === 0 && (
                <span className="text-xs text-fg-subtle">
                  No repos in this project yet — create one first.
                </span>
              )}
            </div>

            {submitError && (
              <p className="text-sm text-danger font-mono break-all">
                {submitError}
              </p>
            )}

            {result && (
              <div className="text-sm space-y-2 pt-2 border-t border-border/40">
                <p className="text-fg">
                  Resolved <strong>{result.total_artifacts}</strong> artifact
                  {result.total_artifacts === 1 ? '' : 's'} from this build
                  {result.skipped_not_in_registry > 0 && (
                    <>
                      {' '}· skipped <strong>{result.skipped_not_in_registry}</strong>{' '}
                      module artifact{result.skipped_not_in_registry === 1 ? '' : 's'}{' '}
                      that the registry has never seen
                    </>
                  )}
                  {result.skipped_already_in_target > 0 && (
                    <>
                      {' '}· skipped <strong>{result.skipped_already_in_target}</strong>{' '}
                      already in the target repo
                    </>
                  )}
                  .
                </p>
                {result.promotions.length === 0 ? (
                  <p className="text-fg-muted text-xs">
                    Nothing to copy — every matching artifact was already in the
                    target repo.
                  </p>
                ) : (
                  <ul className="text-xs space-y-1">
                    {result.promotions.map((p) => (
                      <li key={p.id} className="font-mono text-fg-muted">
                        <Link
                          to="/promotions"
                          className="text-accent hover:underline"
                        >
                          {p.id.slice(0, 8)}…
                        </Link>{' '}
                        {p.src_slug} → {p.dst_slug} · {p.artifact_count} artifact
                        {p.artifact_count === 1 ? '' : 's'} · {p.status}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Source" />
        <CardBody className="space-y-2 text-sm">
          {data.vcs_revision ? (
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-fg-subtle" />
              <code className="font-mono">{shortRev(data.vcs_revision)}</code>
              {data.vcs_url && (
                <a
                  href={data.vcs_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  {data.vcs_url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ) : (
            <p className="text-fg-muted">No source-control metadata recorded.</p>
          )}
          {(data.agent_name || data.agent_version) && (
            <div className="text-fg-muted">
              <span className="text-fg-subtle">Agent:</span>{' '}
              {data.agent_name}
              {data.agent_version && <span> · {data.agent_version}</span>}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Box className="h-4 w-4 text-accent" />
              Artifacts
            </span>
          }
          description={`${data.artifacts.length} module artifact${data.artifacts.length === 1 ? '' : 's'} declared`}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Module</th>
                <th className="px-4 py-2 font-medium">Artifact</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {data.artifacts.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={4}>
                    No artifacts denormalised — the submitted body had no{' '}
                    <code className="font-mono mx-1">modules[].artifacts[]</code>{' '}
                    entries.
                  </td>
                </tr>
              )}
              {data.artifacts.map((a, i) => (
                <tr
                  key={`${a.module_id}/${a.artifact_name}/${i}`}
                  className="border-b border-border/50"
                >
                  <td className="px-4 py-2 font-mono text-xs">{a.module_id}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.artifact_name}</td>
                  <td className="px-4 py-2">
                    {a.artifact_type ? (
                      <Badge tone="neutral">{a.artifact_type}</Badge>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                    {a.sha256 ? a.sha256.slice(0, 16) + '…' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Layers className="h-4 w-4 text-accent" />
              Dependencies
            </span>
          }
          description={`${data.dependencies.length} consumed input${data.dependencies.length === 1 ? '' : 's'} declared. Click a coordinate to find every other build that consumed the same library.`}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Module</th>
                <th className="px-4 py-2 font-medium">Coordinate</th>
                <th className="px-4 py-2 font-medium">Scope</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {data.dependencies.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={5}>
                    No dependencies denormalised — the submitted body had no{' '}
                    <code className="font-mono mx-1">modules[].dependencies[]</code>{' '}
                    entries.
                  </td>
                </tr>
              )}
              {data.dependencies.map((d, i) => (
                <tr
                  key={`${d.module_id}/${d.dep_id}/${d.scope ?? ''}/${i}`}
                  className="border-b border-border/50 hover:bg-bg-hover/40"
                >
                  <td className="px-4 py-2 font-mono text-xs">{d.module_id}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link
                      to={`/projects/${id}/builds/dependency-search?q=${encodeURIComponent(d.dep_id)}&mode=coord`}
                      className="text-accent hover:underline inline-flex items-center gap-1"
                      title="Find every build in this project that consumed this coordinate"
                    >
                      <Search className="h-3 w-3" />
                      {d.dep_id}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    {d.scope ? (
                      <Badge tone="neutral">{d.scope}</Badge>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {d.dep_type ? (
                      <Badge tone="neutral">{d.dep_type}</Badge>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                    {d.sha256 ? d.sha256.slice(0, 16) + '…' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Tag className="h-4 w-4 text-accent" />
              Properties
            </span>
          }
          description="Top-level CI-supplied properties (env, build args, custom tags)."
        />
        <CardBody>
          {propEntries.length === 0 ? (
            <p className="text-fg-muted text-sm">No properties recorded.</p>
          ) : (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {propEntries.map(([k, v]) => (
                <div key={k} className="flex gap-2 min-w-0">
                  <dt className="font-mono text-xs text-fg-muted shrink-0">{k}</dt>
                  <dd className="font-mono text-xs break-all">{v}</dd>
                </div>
              ))}
            </dl>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={
            <button
              type="button"
              onClick={() => setShowBody(!showBody)}
              className="inline-flex items-center gap-2 hover:text-accent transition-colors"
            >
              {showBody ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Raw Build-Info JSON
            </button>
          }
          description="The original body the CI submitted, JFrog-format. Useful for debugging tooling that publishes through /api/build."
        />
        {showBody && (
          <CardBody>
            <pre className="text-xs font-mono bg-bg-inset rounded-md p-4 overflow-auto max-h-96">
              {JSON.stringify(data.body, null, 2)}
            </pre>
          </CardBody>
        )}
      </Card>
    </div>
  );
}
